import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import { i18n } from "../../translate/i18n";

function IconChevronDown({ open }) {
	return (
		<svg
			className={`size-4 shrink-0 text-default-500 transition-transform${open ? " rotate-180" : ""}`}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			aria-hidden
		>
			<polyline points="6 9 12 15 18 9" />
		</svg>
	);
}

function IconSearch() {
	return (
		<svg
			className="size-3.5 text-default-500"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			aria-hidden
		>
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function TagBadge({ tag, onRemove }) {
	const color = tag.color || "#64748b";

	return (
		<button
			type="button"
			className="tailwick-tags-multi__badge"
			style={{
				backgroundColor: `${color}14`,
				color,
				borderColor: `${color}40`,
			}}
			onClick={() => onRemove(tag.id)}
			title={tag.name}
		>
			<span className="tailwick-tags-multi__badge-dot" style={{ backgroundColor: color }} />
			<span className="tailwick-tags-multi__badge-label">{tag.name}</span>
			<span className="tailwick-tags-multi__badge-remove">×</span>
		</button>
	);
}

/**
 * Multi-seleção de etiquetas — dropdown com busca e listagem.
 */
export default function ChatTagsMultiSelect({
	tags = [],
	selectedTagIds = [],
	onChange,
	menuPlacement = "dropdown",
}) {
	const containerRef = useRef(null);
	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	const selectedTags = useMemo(
		() => tags.filter((tag) => selectedTagIds.indexOf(tag.id) > -1),
		[tags, selectedTagIds]
	);

	const filteredTags = useMemo(() => {
		const normalized = searchTerm.trim().toLowerCase();
		if (!normalized) return tags;
		return tags.filter((tag) =>
			String(tag.name || "").toLowerCase().includes(normalized)
		);
	}, [searchTerm, tags]);

	const summary =
		selectedTags.length === 0
			? i18n.t("chat.layout.filterTagsPlaceholder")
			: selectedTags.length <= 2
				? selectedTags.map((tag) => tag.name).join(", ")
				: i18n.t("contacts.filterTagsSelectedCount", { count: selectedTags.length });

	useEffect(() => {
		if (!open) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		if (menuPlacement === "dropdown") {
			document.addEventListener("mousedown", handleClickOutside);
			return () => document.removeEventListener("mousedown", handleClickOutside);
		}

		return undefined;
	}, [open, menuPlacement]);

	useEffect(() => {
		if (!open || menuPlacement !== "inline") return;
		containerRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, [open, menuPlacement]);

	const toggleTag = (tagId) => {
		if (selectedTagIds.indexOf(tagId) > -1) {
			onChange(selectedTagIds.filter((id) => id !== tagId));
		} else {
			onChange([...selectedTagIds, tagId]);
		}
	};

	const handleClear = () => {
		onChange([]);
		setSearchTerm("");
	};

	return (
		<div ref={containerRef} className="tailwick-tags-multi">
			<div className="tailwick-tags-multi__dropdown relative">
				<button
					type="button"
					className="form-input form-input-sm tailwick-tags-multi__trigger flex w-full items-center justify-between gap-3 text-left"
					onClick={() => setOpen((current) => !current)}
					aria-expanded={open}
					aria-haspopup="listbox"
				>
					<span
						className={`truncate ${
							selectedTags.length > 0
								? "text-default-800"
								: "text-default-500"
						}`}
					>
						{summary}
					</span>
					<IconChevronDown open={open} />
				</button>

				{open && (
					<div
						className={clsx(
							"tailwick-tags-multi__menu",
							menuPlacement === "inline" && "tailwick-tags-multi__menu--inline"
						)}
						role="listbox"
					>
						<div className="tailwick-tags-multi__search-wrap">
							<input
								type="search"
								className="form-input form-input-sm tailwick-tags-multi__search"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder={i18n.t("chat.layout.filterTagsSearch")}
								autoFocus
							/>
							<div className="tailwick-tags-multi__search-icon">
								<IconSearch />
							</div>
						</div>

						<div className="tailwick-tags-multi__list">
							{filteredTags.length > 0 ? (
								filteredTags.map((tag) => {
									const checked = selectedTagIds.indexOf(tag.id) > -1;
									const color = tag.color || "#64748b";

									return (
										<label key={tag.id} className="tailwick-tags-multi__option">
											<input
												type="checkbox"
												className="tailwick-filter-panel__checkbox"
												checked={checked}
												onChange={() => toggleTag(tag.id)}
											/>
											<span
												className="tailwick-filter-panel__color-dot"
												style={{ backgroundColor: color }}
											/>
											<span className="tailwick-tags-multi__option-label">
												{tag.name}
											</span>
										</label>
									);
								})
							) : (
								<p className="tailwick-filter-panel__empty">
									{tags.length > 0
										? i18n.t("chat.layout.filterTagsNoResults")
										: i18n.t("chat.layout.filterTagsEmpty")}
								</p>
							)}
						</div>

						{selectedTags.length > 0 && (
							<div className="tailwick-tags-multi__menu-footer">
								<button
									type="button"
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary"
									onClick={handleClear}
								>
									{i18n.t("contacts.filterTagsClear")}
								</button>
							</div>
						)}
					</div>
				)}
			</div>

			{selectedTags.length > 0 && (
				<div className="tailwick-tags-multi__selected">
					{selectedTags.map((tag) => (
						<TagBadge key={tag.id} tag={tag} onRemove={toggleTag} />
					))}
				</div>
			)}
		</div>
	);
}
